/* eslint-disable @typescript-eslint/camelcase */
import { Grid } from '@material-ui/core';
import Button from 'components/Button';
import Permission from 'components/ValidPermission';
import i18n from 'i18n';
import {
  applyAdvisorRequest,
  ignoreAdvisorRequest,
  revertAdvisorRequest,
} from 'modules/advisors/actions';
import { AdvisorDetailsProps } from 'modules/advisors/interfaces';
import { PermissionsData } from 'modules/authenticate/interfaces';
import { editEnvironmentsRequest } from 'modules/environments/actions';
import { modalClose, modalOpen } from 'modules/modal/actions';
import { ModalFields } from 'modules/modal/interfaces';
import React from 'react';
import { connect } from 'react-redux';
import ApplicationState from 'store/interfaces';
import IgnoreMessage from './ignoreMessage';

interface Props {
  advisorDetails: AdvisorDetailsProps | null;
  advisorUuid: string;
  isLoading: boolean;
  permissions?: PermissionsData | null;
  modalOpen: (props: ModalFields) => void;
  modalClose: () => void;
  onClose: () => void;
  applyAdvisorRequest: (props) => void;
  editEnvironmentsRequest: ({
    id,
    values,
    shouldGetEnvironmentOnSuccess,
  }) => void;
  revertAdvisorRequest: (props) => void;
  ignoreAdvisorRequest: (props) => void;
  getAdvisors: (refresh?: boolean) => void;
}

const AdvisorButtons = ({
  advisorDetails,
  advisorUuid,
  isLoading,
  permissions,
  applyAdvisorRequest,
  editEnvironmentsRequest,
  revertAdvisorRequest,
  ignoreAdvisorRequest,
  modalOpen,
  modalClose,
  getAdvisors,
}: Props) => {
  const handleIgnore = () => {
    modalOpen({
      title: i18n.t('ADVISOR_PAGE.WANT_IGNORE_ADVISOR_KEY'),
      content: (
        <IgnoreMessage
          onClose={() => modalClose()}
          onConfirm={ignored_reason => {
            ignoreAdvisorRequest({
              advisorUuid,
              ignored_reason,
              getAdvisorsFn: () => getAdvisors(),
            });
            modalClose();
          }}
        />
      ),
      noBorder: true,
    });
  };

  const suggestApplyAutomaticallyActivation = () => {
    modalOpen({
      title: i18n.t('SUGGEST_APPLY_ADVISOR_AUTOMATICALLY_TITLE_KEY'),
      content: i18n.t('SUGGEST_APPLY_ADVISOR_AUTOMATICALLY_DESCRIPTION_KEY'),
      footer: (
        <Grid>
          <Button
            submitButton
            id="confirm-button"
            onClick={() => {
              editEnvironmentsRequest({
                id: advisorDetails?.environment?.environment_uuid,
                values: { should_apply_advisor_automatically: true },
                shouldGetEnvironmentOnSuccess: false,
              });
              modalClose();
            }}
          >
            {i18n.t('CONFIRM_KEY')}
          </Button>
          <Button id="cancel-button" variant="outlined" onClick={modalClose}>
            {i18n.t('CANCEL_KEY')}
          </Button>
        </Grid>
      ),
    });
  };

  const handleApply = () => {
    modalOpen({
      title: i18n.t('ADVISOR_PAGE.WANT_APPLY_ADVISOR_KEY'),
      content: i18n.t('ADVISOR_PAGE.CONFIRM_APPLY_ADVISOR_KEY'),
      footer: (
        <Grid>
          <Button
            submitButton
            id="confirm-button"
            onClick={() => {
              applyAdvisorRequest({
                advisorUuid,
                getAdvisorsFn: () => getAdvisors(),
              });

              if (
                !advisorDetails?.environment
                  ?.environment_should_apply_advisor_automatically &&
                permissions?.edit_environment
              ) {
                suggestApplyAutomaticallyActivation();
              } else {
                modalClose();
              }
            }}
          >
            {i18n.t('CONFIRM_KEY')}
          </Button>
          <Button id="cancel-button" variant="outlined" onClick={modalClose}>
            {i18n.t('CANCEL_KEY')}
          </Button>
        </Grid>
      ),
    });
  };

  const handleRevert = () => {
    modalOpen({
      title: i18n.t('ADVISOR_PAGE.WANT_REVERT_ADVISOR_KEY'),
      content: i18n.t('ADVISOR_PAGE.CONFIRM_REVERT_ADVISOR_KEY'),
      footer: (
        <Grid>
          <Button
            submitButton
            id="confirm-button"
            onClick={() => {
              revertAdvisorRequest({
                advisorUuid,
                getAdvisorsFn: () => getAdvisors(),
              });
              modalClose();
            }}
          >
            {i18n.t('CONFIRM_KEY')}
          </Button>
          <Button id="cancel-button" variant="outlined" onClick={modalClose}>
            {i18n.t('CANCEL_KEY')}
          </Button>
        </Grid>
      ),
    });
  };

  return (
    <>
      <Grid container justify="center">
        {advisorDetails?.status === 'pending' && (
          <>
            <Permission value="can_apply_scaling_advisor">
              <Button
                id="apply-button"
                onClick={handleApply}
                isLoading={isLoading}
                submitButton
              >
                {i18n.t('APPLY_KEY')}
              </Button>
            </Permission>
            <Permission value="can_ignore_scaling_advisor">
              <Button
                id="cancel-button"
                variant="outlined"
                onClick={handleIgnore}
                isLoading={isLoading}
              >
                {i18n.t('IGNORE_KEY')}
              </Button>
            </Permission>
          </>
        )}
        {advisorDetails?.status === 'applied' && (
          <Permission value="can_revert_scaling_advisor">
            <Button
              id="revert-button"
              variant="outlined"
              onClick={handleRevert}
              isLoading={isLoading}
            >
              {i18n.t('REVERT_KEY')}
            </Button>
          </Permission>
        )}
        <Button
          id="cancel-button"
          variant="outlined"
          onClick={modalClose}
          isLoading={isLoading}
          deleteButton
        >
          {i18n.t('CLOSE_KEY')}
        </Button>
      </Grid>
    </>
  );
};

const mapStateToProps = ({
  authenticate: { data },
  advisors: { advisorDetails },
}: ApplicationState) => ({
  advisorDetails,
  permissions: data?.permissions,
});

export default connect(mapStateToProps, {
  applyAdvisorRequest,
  editEnvironmentsRequest,
  ignoreAdvisorRequest,
  revertAdvisorRequest,
  modalOpen,
  modalClose,
})(AdvisorButtons);
